import { Brand } from '@axo/config/@types/Brand.types';
import {
  getConsent,
  getConsentConfig,
  getConsentItemsByCustomer,
  getConsentPartners,
  updateConsentItemsByCustomer,
} from '@axo/shared/data-access/api/consent';
import { useAPI } from '@axo/shared/data-access/provider';
import { ConfigType } from '@axo/shared/data-access/types/consent';
import { MarketCountryCode } from '@axo/shared/types';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { consentKeys, parseConsentKey } from '../cache-keys/consentKeys';
import { queryFunction } from '../query-function';

type useGetCustomerConsentArgs = {
  country: MarketCountryCode;
  configType: ConfigType;
  customerID: string;
};

export function useGetCustomerConsent({
  country,
  configType,
  customerID,
}: useGetCustomerConsentArgs) {
  const api = useAPI();
  const cacheKey = consentKeys.user(country, customerID, configType);

  return useQuery(
    cacheKey,
    queryFunction(getConsent(api), ({ queryKey }) => ({
      ...parseConsentKey(queryKey),
    })),
    {
      enabled: api.token.length > 0 && !!customerID,
    }
  );
}

type useGetConsentConfigArgs = {
  country: MarketCountryCode;
  brandID: Brand;
  configType?: ConfigType;
  enabled?: boolean;
};

export function useGetConsentConfig({
  country,
  brandID,
  configType,
  enabled,
}: useGetConsentConfigArgs) {
  const api = useAPI();
  const cacheKey = consentKeys.config(country, brandID, configType);

  return useQuery(
    cacheKey,
    queryFunction(getConsentConfig(api), ({ queryKey }) => ({
      ...parseConsentKey(queryKey),
    })),
    {
      enabled,
    }
  );
}

export function useGetConsentPartners() {
  const api = useAPI();
  const cacheKey = consentKeys.root();

  return useQuery(
    cacheKey,
    queryFunction(getConsentPartners(api), ({ queryKey }) =>
      parseConsentKey(queryKey)
    ),
    {
      enabled: api.token.length > 0,
    }
  );
}

export function useGetConsentItemsByCustomer(
  country?: MarketCountryCode,
  customerID?: string
) {
  const api = useAPI();
  const cacheKey = consentKeys.items(country, customerID);

  return useQuery(
    cacheKey,
    queryFunction(getConsentItemsByCustomer(api), ({ queryKey }) =>
      parseConsentKey(queryKey)
    ),
    {
      enabled: api.token.length > 0 && !!customerID && !!country,
    }
  );
}

interface IUseUpdateConsent {
  country: MarketCountryCode;
  customerID: string;
  patchData: any;
}

export function usePatchPartnerConsent() {
  const api = useAPI();
  const client = useQueryClient();

  return useMutation(
    ({ country, customerID, patchData }: IUseUpdateConsent) =>
      updateConsentItemsByCustomer(api)({
        country,
        customerID,
        patchData,
      }),
    {
      onSuccess: (_, params) => {
        return client.invalidateQueries(
          consentKeys.items(params.country, params.customerID)
        );
      },
    }
  );
}
